import { PlayerData } from './kyp-search';

// Regular expression to find article references in the format "Title of Article" or similar patterns
const ARTICLE_REFERENCE_REGEX = /"([^"]+)"|'([^']+)'|"([^"]+)"|"([^"]+)"|'([^']+)'|«([^»]+)»/g;

// Process historical search results to convert string JSON to proper objects
export function processHistoricalSearchResults(results: any): PlayerData {
  const processedData: PlayerData = {};
  
  // First, check for overall_player_score at the top level
  if (results.overall_player_score !== undefined) {
    processedData.overall_player_score = results.overall_player_score;
  }
  
  // Extract all article references from the original data
  const articleReferences: Record<string, string> = {};
  
  // Look for article URLs in the original data
  if (results.articles && Array.isArray(results.articles)) {
    console.log('Found articles array with', results.articles.length, 'items');
    results.articles.forEach((article: any) => {
      if (article.title && article.url) {
        articleReferences[article.title.trim()] = article.url;
      }
    });
  }
  
  // Also check for article_references directly in the results
  if (results.article_references && typeof results.article_references === 'object') {
    console.log('Found article_references object with', Object.keys(results.article_references).length, 'items');
    Object.entries(results.article_references).forEach(([title, url]) => {
      if (title && url) {
        // Handle case where article_references might be a stringified JSON object
        if (typeof url === 'string') {
          // Check if the URL is actually a stringified JSON object
          if (url.startsWith('{') && url.endsWith('}')) {
            try {
              const parsedUrl = JSON.parse(url);
              // If it parsed successfully and has a url property, use that
              if (parsedUrl && typeof parsedUrl === 'object' && parsedUrl.url) {
                articleReferences[title.trim()] = parsedUrl.url;
              } else {
                // Otherwise just use the string as is
                articleReferences[title.trim()] = url;
              }
            } catch (e) {
              // If parsing fails, use the string as is
              articleReferences[title.trim()] = url;
            }
          } else {
            // Regular string URL
            articleReferences[title.trim()] = url;
          }
        } else if (typeof url === 'object' && url !== null && 'url' in url) {
          // Handle case where url is already an object with a url property
          articleReferences[title.trim()] = url.url as string;
        } else {
          // Fallback - convert to string
          articleReferences[title.trim()] = String(url);
        }
      }
    });
  }
  
  // Check for stringified JSON article references
  if (typeof results.article_references === 'string') {
    try {
      const parsedRefs = JSON.parse(results.article_references);
      if (parsedRefs && typeof parsedRefs === 'object') {
        Object.entries(parsedRefs).forEach(([title, url]) => {
          if (title && url) {
            articleReferences[title.trim()] = url as string;
          }
        });
      }
    } catch (e) {
      console.log('Failed to parse article_references string:', e);
    }
  }
  
  console.log('Extracted article references:', articleReferences);
  
  // Process each category in the results
  Object.entries(results).forEach(([key, value]) => {
    // Skip non-category fields and already processed fields
    if (['status', 'message', 'player_id', 'overall_player_score', 'articles', 'article_references'].includes(key)) {
      return;
    }
    
    if (typeof value === 'string') {
      try {
        // Try to parse the string as JSON
        const parsedValue = JSON.parse(value);
        
        // Special handling for rating data
        if (parsedValue && typeof parsedValue === 'object') {
          // Handle nested summary structure
          if (parsedValue.summary && typeof parsedValue.summary === 'object') {
            // If summary contains rating, ensure it's properly processed
            if (parsedValue.summary.rating) {
              if (typeof parsedValue.summary.rating === 'string') {
                try {
                  parsedValue.summary.rating = JSON.parse(parsedValue.summary.rating);
                } catch (e) {
                  console.log(`Failed to parse rating string in summary for ${key}:`, e);
                }
              }
            }
          }
          
          // Also check for direct rating property
          if (parsedValue.rating) {
            if (typeof parsedValue.rating === 'string') {
              try {
                parsedValue.rating = JSON.parse(parsedValue.rating);
              } catch (e) {
                console.log(`Failed to parse rating string for ${key}:`, e);
              }
            }
          }
          
          // Check if this section has its own article_references
          if (!parsedValue.article_references && Object.keys(articleReferences).length > 0) {
            // Add article references if we have any matches and there's a summary
            const summaryText = typeof parsedValue.summary === 'string' 
              ? parsedValue.summary 
              : (parsedValue.summary?.summary || '');
              
            if (summaryText) {
              parsedValue.article_references = extractArticleReferences(summaryText, articleReferences);
            }
          }
        }
        
        processedData[key as keyof PlayerData] = parsedValue;
      } catch (e) {
        // If parsing fails, use the string value directly
        console.log(`Failed to parse JSON for ${key}:`, e);
        const summaryObj = { summary: value };
        
        // Add article references if we have any matches
        if (Object.keys(articleReferences).length > 0) {
          summaryObj.article_references = extractArticleReferences(value, articleReferences);
        }
        
        processedData[key as keyof PlayerData] = summaryObj;
      }
    } else if (typeof value === 'object' && value !== null) {
      // If it's already an object, ensure nested objects are properly processed
      const processedObject = { ...value };
      
      // Process summary if it exists
      if (processedObject.summary && typeof processedObject.summary === 'object') {
        // Process rating within summary
        if (processedObject.summary.rating && typeof processedObject.summary.rating === 'string') {
          try {
            processedObject.summary.rating = JSON.parse(processedObject.summary.rating);
          } catch (e) {
            console.log(`Failed to parse rating string in summary for ${key}:`, e);
          }
        }
      }
      
      // Process direct rating property
      if (processedObject.rating && typeof processedObject.rating === 'string') {
        try {
          processedObject.rating = JSON.parse(processedObject.rating);
        } catch (e) {
          console.log(`Failed to parse rating string for ${key}:`, e);
        }
      }
      
      // Check if this section has its own article_references
      if (!processedObject.article_references && Object.keys(articleReferences).length > 0) {
        // Add article references if we have any matches and there's a summary
        const summaryText = typeof processedObject.summary === 'string' 
          ? processedObject.summary 
          : (typeof processedObject.summary === 'object' ? processedObject.summary?.summary || '' : '');
          
        if (summaryText) {
          processedObject.article_references = extractArticleReferences(summaryText, articleReferences);
        }
      }
      
      processedData[key as keyof PlayerData] = processedObject;
    } else {
      // For other types, use directly
      processedData[key as keyof PlayerData] = value as any;
    }
  });
  
  // Store the article references at the top level as well
  if (Object.keys(articleReferences).length > 0) {
    // Ensure all article references are properly formatted
    const formattedReferences: Record<string, string> = {};
    
    Object.entries(articleReferences).forEach(([title, url]) => {
      // Handle case where the URL might be a stringified JSON
      if (typeof url === 'string') {
        if (url.startsWith('{') && url.endsWith('}')) {
          try {
            const parsedUrl = JSON.parse(url);
            if (parsedUrl && typeof parsedUrl === 'object' && parsedUrl.url) {
              formattedReferences[title] = parsedUrl.url;
            } else {
              formattedReferences[title] = url;
            }
          } catch (e) {
            formattedReferences[title] = url;
          }
        } else {
          formattedReferences[title] = url;
        }
      } else if (typeof url === 'object' && url !== null && 'url' in url) {
        formattedReferences[title] = url.url as string;
      } else {
        formattedReferences[title] = String(url);
      }
    });
    
    // Store article references in a special property with a name that will be explicitly filtered out
    processedData._article_references_for_final_section = formattedReferences;
    console.log('Added article references to special property with', Object.keys(formattedReferences).length, 'items');
    
    // IMPORTANT: Do NOT add article_references as a top-level property to avoid duplicate sections
  }
  
  // Special handling for the case where article_references is a JSON string
  if (typeof results.article_references === 'string' && results.article_references.startsWith('{')) {
    try {
      const parsedArticles = JSON.parse(results.article_references);
      if (parsedArticles && typeof parsedArticles === 'object') {
        const formattedReferences: Record<string, string> = {};
        
        Object.entries(parsedArticles).forEach(([title, url]) => {
          if (title && url) {
            formattedReferences[title] = typeof url === 'string' ? url : String(url);
          }
        });
        
        if (Object.keys(formattedReferences).length > 0) {
          // Store article references in a special property with a name that will be explicitly filtered out
          processedData._article_references_for_final_section = formattedReferences;
          console.log('Parsed article references to special property with', Object.keys(formattedReferences).length, 'items');
          
          // IMPORTANT: Do NOT add article_references as a top-level property to avoid duplicate sections
        }
      }
    } catch (e) {
      console.error('Failed to parse article_references JSON string:', e);
    }
  }
  
  // Special handling for debug.summary.all_articles
  if (results.debug?.summary?.all_articles) {
    console.log('Found debug.summary.all_articles with', results.debug.summary.all_articles.length, 'items');
    processedData.debug = processedData.debug || {};
    processedData.debug.summary = processedData.debug.summary || {};
    processedData.debug.summary.all_articles = results.debug.summary.all_articles;
    
    // Also add these articles to the top-level article_references for convenience
    if (Array.isArray(results.debug.summary.all_articles)) {
      const formattedReferences: Record<string, string> = { ...processedData.article_references || {} };
      
      results.debug.summary.all_articles.forEach((article: any) => {
        if (article.title && article.url) {
          formattedReferences[article.title] = article.url;
        }
      });
      
      if (Object.keys(formattedReferences).length > 0) {
        processedData.article_references = formattedReferences;
        console.log('Added debug.summary.all_articles to article_references, now with', Object.keys(formattedReferences).length, 'items');
      }
    }
  }
  
  // Log the processed data for debugging
  console.log('Processed historical search results:', processedData);
  
  return processedData;
}

/**
 * Extracts article references from a text by looking for quoted titles
 * and matching them against known article titles
 */
function extractArticleReferences(text: string, articleReferences: Record<string, string>): Record<string, string> {
  const foundReferences: Record<string, string> = {};
  const articleTitles = Object.keys(articleReferences);
  
  // First try to find exact matches of article titles in the text
  articleTitles.forEach(title => {
    if (text.includes(title)) {
      foundReferences[title] = articleReferences[title];
    }
  });
  
  // Then look for quoted text that might be article references
  let match;
  const regex = new RegExp(ARTICLE_REFERENCE_REGEX); // Create a new regex instance to reset lastIndex
  while ((match = regex.exec(text)) !== null) {
    // The regex has multiple capture groups for different quote styles
    // Find the one that matched
    const quotedText = match[1] || match[2] || match[3] || match[4] || match[5] || match[6];
    
    if (quotedText) {
      // Look for the closest matching article title
      const bestMatch = findBestMatch(quotedText, articleTitles);
      if (bestMatch) {
        foundReferences[quotedText] = articleReferences[bestMatch];
      }
    }
  }
  
  return foundReferences;
}

/**
 * Finds the best matching article title for a quoted reference
 */
function findBestMatch(quotedText: string, articleTitles: string[]): string | null {
  // First try exact match
  if (articleTitles.includes(quotedText)) {
    return quotedText;
  }
  
  // Then try case-insensitive match
  const lowerQuoted = quotedText.toLowerCase();
  for (const title of articleTitles) {
    if (title.toLowerCase() === lowerQuoted) {
      return title;
    }
  }
  
  // Then try partial match (quoted text is part of a title)
  for (const title of articleTitles) {
    if (title.toLowerCase().includes(lowerQuoted)) {
      return title;
    }
  }
  
  // Then try if title is part of quoted text
  for (const title of articleTitles) {
    if (lowerQuoted.includes(title.toLowerCase())) {
      return title;
    }
  }
  
  // No good match found
  return null;
}

// Function to extract the actual search results from the API response
export function extractSearchResults(data: any): any {
  if (!data) return {};
  
  // Remove status and message fields
  const { status, message, player_id, ...playerDataFields } = data;
  
  return playerDataFields;
}